export function initialOf(name) {
  const trimmed = (name || "").trim();
  return trimmed ? trimmed[0].toUpperCase() : "?";
}

export function displayName(user) {
  if (!user) return "Unknown diver";
  return user.display_name && user.display_name.length > 0 ? user.display_name : user.username;
}

/** Escapes a string for safe insertion into innerHTML. */
export function esc(str) {
  const div = document.createElement("div");
  div.textContent = str ?? "";
  return div.innerHTML;
}

/** A web address in text someone wrote: http://, https:// or www., up to a space.
 *  The same rule as the app's (flutter-app/lib/utils/links.dart). */
const LINK_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;
/** Punctuation after an address that ends the sentence rather than the address. */
const TRAILING_PUNCTUATION = /[.,;:!?'’"”…]+$/;

/** The address without what ends the sentence around it: "(see example.com)."
 *  links example.com. A closing bracket stays when the address opened one. */
function trimAddress(address) {
  let trimmed = address;
  for (;;) {
    const shorter = trimmed.replace(TRAILING_PUNCTUATION, "");
    if (shorter !== trimmed) {
      trimmed = shorter;
    } else if (trimmed.endsWith(")") && trimmed.split("(").length < trimmed.split(")").length) {
      trimmed = trimmed.slice(0, -1);
    } else {
      return trimmed;
    }
  }
}

/**
 * Text someone wrote, escaped for innerHTML as esc() does, with its web addresses
 * made links. They open in a new tab, and tell the site neither where they were
 * clicked (a private cohort's page, say) nor that we vouch for them.
 */
export function linkify(text) {
  const source = text ?? "";
  let html = "";
  let last = 0;
  for (const match of source.matchAll(LINK_PATTERN)) {
    const address = trimAddress(match[0]);
    if (/^(?:https?:\/\/|www\.)$/i.test(address)) continue; // nothing after the start
    const href = /^www\./i.test(address) ? `https://${address}` : address;
    html += esc(source.slice(last, match.index));
    html += `<a href="${esc(href)}" class="dc-user-link" target="_blank" rel="noopener noreferrer nofollow ugc">${
      esc(address)}</a>`;
    last = match.index + address.length;
  }
  return html + esc(source.slice(last));
}

/** The icons before a cohort's name on its chips: the group, and a padlock
 *  after it when the cohort is private. */
export function cohortIcons(community) {
  return `<i class="bi bi-people me-1"></i>${community?.is_private
    ? '<i class="bi bi-lock-fill me-1 private-cohort-icon" title="Private cohort" aria-label="Private cohort"></i>'
    : ""}`;
}

/** HTML for a circular initials avatar. Was copy-pasted as an inline
 * template literal across five call sites (postCard.js, postDetail.js x2,
 * communityMembers.js, userProfile.js) with slightly different sizing —
 * centralized here so any future styling change only needs to happen
 * once. `className` is appended to the base "dc-avatar" class (e.g. for
 * spacing utilities or the "dc-avatar-lg" size variant), and `attrs` is
 * for anything a specific call site needs beyond styling, such as
 * postCard.js's data-user-id used for click delegation. */
export function avatarHtml(name, { sizePx, fontSize, className = "", attrs = "" } = {}) {
  const style = sizePx
    ? ` style="width:${sizePx}px;height:${sizePx}px;${fontSize ? `font-size:${fontSize};` : ""}"`
    : "";
  const classes = ["dc-avatar", className].filter(Boolean).join(" ");
  return `<div class="${classes}"${style}${attrs ? ` ${attrs}` : ""}>${esc(initialOf(name))}</div>`;
}

export function formatDateTime(isoString) {
  const d = new Date(isoString);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDate(isoString) {
  const d = new Date(isoString);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function timeAgo(isoString) {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  const steps = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, secondsInUnit] of steps) {
    const value = Math.floor(seconds / secondsInUnit);
    if (value >= 1) return `${value} ${unit}${value > 1 ? "s" : ""} ago`;
  }
  return "just now";
}

export function toast(message, variant = "primary") {
  // index.html ships the container, but toast() is called from error paths
  // that must not themselves throw — so create it if it is not there.
  let container = document.getElementById("toast-container");
  if (!container) {
    container = document.createElement("div");
    container.id = "toast-container";
    container.className = "position-fixed bottom-0 end-0 p-3";
    container.style.zIndex = "1080";
    document.body.appendChild(container);
  }
  const el = document.createElement("div");
  el.className = `toast align-items-center text-bg-${variant} border-0`;
  el.setAttribute("role", "alert");
  el.innerHTML = `
    <div class="d-flex">
      <div class="toast-body">${esc(message)}</div>
      <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
    </div>`;
  container.appendChild(el);
  const bsToast = new bootstrap.Toast(el, { delay: 4000 });
  bsToast.show();
  el.addEventListener("hidden.bs.toast", () => el.remove());
}

export function difficultyBadgeClass(difficulty) {
  return `badge badge-difficulty-${difficulty || "beginner"}`;
}

/** "City, State, Country" from a dive site, skipping missing parts.
 * Returns null if none of the three are set — mirrors DiveSite.locationLabel
 * in the Flutter app. */
export function siteLocationLabel(site) {
  const parts = [site.city, site.state_province, site.country].filter(
    (p) => p && p.length > 0
  );
  return parts.length ? parts.join(", ") : null;
}

/** Great-circle distance in kilometers between two lat/lng points
 * (haversine formula). Mirrors the identical implementation in the
 * Flutter app's lib/utils/distance.dart so both platforms compute and
 * display distance the same way. */
export function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** "3.2 km away" / "850 m away" */
export function formatDistanceKm(km) {
  if (km < 1) return `${Math.round(km * 1000)} m away`;
  return km < 10 ? `${km.toFixed(1)} km away` : `${Math.round(km)} km away`;
}

/**
 * A coordinate as a number, or NaN if there isn't one.
 *
 * Deliberately not Number(): Number(null), Number(undefined) and
 * Number("") are all 0, so a missing latitude sails through an
 * isFinite check and pins the thing at 0°,0° — a spot in the Gulf of
 * Guinea, plausible enough on a zoomed-out map to pass for data. That
 * bug has been written twice in this codebase, which is why it now
 * lives in one function.
 */
export function parseCoordinate(value) {
  if (value === null || value === undefined || value === "") return NaN;
  return Number(value);
}

/** True when `value` is a usable coordinate within ±limit degrees. */
export function coordinateInRange(value, limit) {
  const parsed = parseCoordinate(value);
  return Number.isFinite(parsed) && Math.abs(parsed) <= limit;
}

/**
 * The certification levels the forms offer.
 *
 * PADI's names, because they are the ones most divers recognise. The API
 * stores free text and accepts anything that fits, so a diver certified
 * by SSI, NAUI, BSAC or CMAS is not shut out — they just will not find
 * their exact grade in this list. Shared by sign-up and profile editing
 * so the two cannot drift apart.
 */
export const CERT_LEVELS = [
  "Open Water",
  "Advanced Open Water",
  "Rescue Diver",
  "Divemaster",
  "Instructor",
  "Technical",
];

/** The <option> list for a certification select, with `selected` set. */
export function certLevelOptionsHtml(current) {
  const chosen = current ?? "";
  const known = CERT_LEVELS.map(
    (level) =>
      `<option value="${esc(level)}"${level === chosen ? " selected" : ""}>${esc(level)}</option>`
  );
  // A level stored by another client, or from an agency this list does
  // not name, must still be shown and survive an unrelated edit — the
  // select would otherwise silently reset it to "Not set" on save.
  if (chosen && !CERT_LEVELS.includes(chosen)) {
    known.push(`<option value="${esc(chosen)}" selected>${esc(chosen)}</option>`);
  }
  return `<option value="">Not set</option>${known.join("")}`;
}
